import type { ApprovalRequest } from "../../../api/approvalRequests.service";
import { MIN_SEARCH_LENGTH } from "../searchConstants";

export function effectiveSearchTerm(raw: string): string | undefined {
  const term = raw.trim();
  return term.length >= MIN_SEARCH_LENGTH ? term : undefined;
}

export type ContentStatus = "NEW" | "APPROVED" | "REJECTED" | "SUSPENDED" | "WITHDRAWN" | "UNKNOWN";

const KNOWN_STATUSES: ReadonlySet<string> = new Set([
  "NEW",
  "APPROVED",
  "REJECTED",
  "SUSPENDED",
  "WITHDRAWN",
]);

export function mapStatus(status: string): ContentStatus {
  return KNOWN_STATUSES.has(status) ? (status as ContentStatus) : "UNKNOWN";
}

export function canApproveOrReject(status: string): boolean {
  const mapped = mapStatus(status);
  return mapped === "NEW" || mapped === "SUSPENDED";
}

export function canSuspend(status: string): boolean {
  const mapped = mapStatus(status);
  return mapped === "NEW" || mapped === "APPROVED";
}

export function isSelectable(status: string): boolean {
  return canApproveOrReject(status) || canSuspend(status);
}

export function getLanguageFlag(langCode: string | null | undefined): string {
  if (!langCode) return "";

  const lang = langCode.toLowerCase().split("-")[0];
  const flagMap: Record<string, string> = {
    en: "🇬🇧",
    pl: "🇵🇱",
    es: "🇪🇸",
    de: "🇩🇪",
    fr: "🇫🇷",
    it: "🇮🇹",
    pt: "🇵🇹",
    zh: "🇨🇳",
    ja: "🇯🇵",
    ko: "🇰🇷",
    ru: "🇷🇺",
    ar: "🇸🇦",
  };

  return flagMap[lang] || langCode.toUpperCase();
}

export function parseTransformations(transformations: string | null | undefined): string[] {
  if (!transformations) return [];

  try {
    const parsed = JSON.parse(transformations);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    if (transformations.includes(",")) {
      return transformations
        .split(",")
        .map(t => t.trim())
        .filter(Boolean);
    }
    return [transformations.trim()].filter(Boolean);
  }

  return [];
}

export function getDisplayContent(req: ApprovalRequest): string {
  return req.contentOutput || req.contentTransformed || req.contentInput || "";
}

export function getPublicationName(req: ApprovalRequest): string {
  return req.publicationName || "";
}

export function getFirmName(req: ApprovalRequest): string {
  return req.firmName || req.firmRef || "";
}

export function getPracticeName(req: ApprovalRequest): string {
  return req.practiceAreaName || req.practiceGroupName || "";
}

export function getUpdatedDate(req: ApprovalRequest): string {
  return req.updatedAt || req.createdAt || "";
}

/**
 * Returns only the primary language subtag from an ISO code (e.g. "en-GB" -> "en", "pl-PL" -> "pl").
 */
export function getLangDisplay(langCode: string | null | undefined): string {
  if (!langCode) return "";
  return langCode.split("-")[0];
}
